import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Stack,
  Grid,
} from '@mui/material';
import { AccountBalance } from '@mui/icons-material';

const BankingInfo = ({ companyInfo, bankDetails = null, variant = 'creator' }) => {
  if (variant === 'creator') {
    const creatorBankDetails = {
      account_holder: companyInfo.account_holder || companyInfo.name || null,
      bank_name: companyInfo.bank_name || null,
      account_number: companyInfo.account_number || null,
      routing_number: companyInfo.routing_number || null,
      swift_code: companyInfo.swift_code || null,
    };
    return (
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Payment Information
        </Typography>
        <Grid container spacing={2.5}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Bank Details
                </Typography>
                <Stack spacing={0.75}>
                  <Typography variant="body2"><strong>Account Holder:</strong> {creatorBankDetails.account_holder || 'Not provided'}</Typography>
                  <Typography variant="body2"><strong>Bank:</strong> {creatorBankDetails.bank_name || 'Not provided'}</Typography>
                  <Typography variant="body2"><strong>Account #:</strong> {creatorBankDetails.account_number || 'Not provided'}</Typography>
                  <Typography variant="body2"><strong>Routing #:</strong> {creatorBankDetails.routing_number || 'Not provided'}</Typography>
                  <Typography variant="body2"><strong>SWIFT:</strong> {creatorBankDetails.swift_code || 'Not provided'}</Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Company Contact
                </Typography>
                <Stack spacing={0.75}>
                  <Typography variant="body2">{companyInfo.name}</Typography>
                  {companyInfo.address_line_1 && (
                    <Typography variant="body2">{companyInfo.address_line_1}</Typography>
                  )}
                  {companyInfo.address_line_2 && (
                    <Typography variant="body2">{companyInfo.address_line_2}</Typography>
                  )}
                  {(companyInfo.city || companyInfo.state || companyInfo.postal_code) && (
                    <Typography variant="body2">
                      {[companyInfo.city, companyInfo.state, companyInfo.postal_code].filter(Boolean).join(', ')}
                    </Typography>
                  )}
                  {companyInfo.country && (
                    <Typography variant="body2">{companyInfo.country}</Typography>
                  )}
                  {companyInfo.phone_number && (
                    <Typography variant="body2"><strong>Phone:</strong> {companyInfo.phone_number}</Typography>
                  )}
                  {companyInfo.email && (
                    <Typography variant="body2"><strong>Email:</strong> {companyInfo.email}</Typography>
                  )}
                  {companyInfo.website && (
                    <Typography variant="body2"><strong>Website:</strong> {companyInfo.website}</Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (variant === 'project') {
    const resolvedBankDetails = {
      account_holder: bankDetails?.account_holder || companyInfo.account_holder || null,
      account_name: bankDetails?.account_name || null,
      bank_name: bankDetails?.bank_name || companyInfo.bank_name || null,
      bank_address: bankDetails?.bank_address || null,
      account_number: bankDetails?.account_number || companyInfo.account_number || null,
      routing_number: bankDetails?.routing_number || companyInfo.routing_number || null,
      swift_code: bankDetails?.swift_code || bankDetails?.swift || companyInfo.swift_code || null,
      beneficiary_address: bankDetails?.beneficiary_address || companyInfo.beneficiary_address || null,
    };
    const hasResolvedBankDetails = Object.values(resolvedBankDetails).some(Boolean);
    if (!hasResolvedBankDetails) {
      return null;
    }
    return (
      <Card sx={{ mb: 3, borderRadius: 3, background: '#ffffff', border: '1px solid #ead7dc', boxShadow: 'none' }}>
        <CardContent sx={{ p: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.1 }}>
            <Avatar sx={{ background: '#fdf1f3', color: '#c63b4a', width: 30, height: 30 }}>
              <AccountBalance sx={{ fontSize: 16 }} />
            </Avatar>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827' }}>
              Banking Information
            </Typography>
          </Box>
          <Grid container spacing={1.25}>
            <Grid item xs={6} md={4}>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, mb: 0.25, color: '#c63b4a' }}>Bank Name</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.84rem', lineHeight: 1.25 }}>{resolvedBankDetails.bank_name || 'Not provided'}</Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, mb: 0.25, color: '#c63b4a' }}>Account Holder</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.84rem', lineHeight: 1.25 }}>{resolvedBankDetails.account_holder || 'Not provided'}</Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, mb: 0.25, color: '#c63b4a' }}>Account Number</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.84rem', lineHeight: 1.25 }}>{resolvedBankDetails.account_number || 'Not provided'}</Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, mb: 0.25, color: '#c63b4a' }}>Routing Number</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.84rem', lineHeight: 1.25 }}>{resolvedBankDetails.routing_number || 'Not provided'}</Typography>
            </Grid>
            <Grid item xs={6} md={4}>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, mb: 0.25, color: '#c63b4a' }}>SWIFT Code</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.84rem', lineHeight: 1.25 }}>{resolvedBankDetails.swift_code || 'Not provided'}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.1, mb: 0.25, color: '#c63b4a' }}>Beneficiary Address</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.84rem', lineHeight: 1.25 }}>
                {resolvedBankDetails.beneficiary_address || 'Not provided'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  }

  // Regular variant
  const regularBankDetails = {
    account_holder: companyInfo.account_holder || companyInfo.name || null,
    bank_name: companyInfo.bank_name || null,
    account_number: companyInfo.account_number || null,
    routing_number: companyInfo.routing_number || null,
    swift_code: companyInfo.swift_code || null,
    bank_address: companyInfo.bank_address || null,
    beneficiary_address: companyInfo.beneficiary_address || companyInfo.address_line_1 || null,
  };

  return (
    <Card sx={{ mb: 5, borderRadius: 3, background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
          <Avatar sx={{ background: '#10b981' }}>
            <AccountBalance />
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Banking Information
          </Typography>
        </Box>
        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Bank Name</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{regularBankDetails.bank_name || 'Not provided'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Account Holder</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{regularBankDetails.account_holder || 'Not provided'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Account Number</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{regularBankDetails.account_number || 'Not provided'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Routing Number</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{regularBankDetails.routing_number || 'Not provided'}</Typography>
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>SWIFT Code</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{regularBankDetails.swift_code || 'Not provided'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Bank Address</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{regularBankDetails.bank_address || 'Not provided'}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>Beneficiary Address</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {regularBankDetails.beneficiary_address || 'Not provided'}
                </Typography>
              </Box>
              
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default BankingInfo;
